import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { API_URL } from "../lib/api";
import { captureSignupSource, getSignupSource } from "../lib/signupSource";
import { Logo } from "../components/landing/Logo";
import { motion } from "framer-motion";

const googleOAuthEnabled = process.env.REACT_APP_GOOGLE_OAUTH_ENABLED === "true";

// Turns an axios error into actionable, specific text — never a generic
// "Something went wrong.". Every branch explains WHY the request failed.
function describeAuthError(err) {
  // axios leaves `response` undefined when the request never got a response
  // at all (backend unreachable, DNS failure, CORS block, connection reset).
  if (!err?.response) {
    return "Cannot connect to server. Please try again.";
  }

  const { status, data } = err.response;
  const detail = data?.detail;

  if (status === 409 && typeof detail === "string" && detail.toLowerCase().includes("already registered")) {
    return "This email is already registered. Try signing in.";
  }

  // FastAPI/Pydantic 422 validation errors: detail is an array of
  // {loc: ["body", "password"], msg: "..."} objects.
  if (status === 422 && Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "This field";
    return `${field}: ${first.msg}`;
  }

  if (typeof detail === "string" && detail) {
    return detail;
  }

  // Unknown shape (e.g. a non-JSON 404/500 from the wrong host) — show the
  // real error rather than hiding it behind a generic string.
  return err?.message || `Request failed with status ${status}.`;
}

const startGoogleAuth = () => {
  const source = getSignupSource();
  window.location.href = `${API_URL}/auth/google/start${source ? `?source=${encodeURIComponent(source)}` : ""}`;
};

export default function Login() {
  const location = useLocation();
  // Direct /login?s=… or /register?s=… links carry attribution too.
  captureSignupSource(location.search);
  // /register is a thin alias onto this same page (landing page CTAs link here)
  // — it just opens straight into the "create account" mode.
  const [mode, setMode] = useState(location.pathname === "/register" ? "register" : "login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  // Google sign-in failures land here as /login?error=oauth (see AuthCallback.jsx).
  const [error, setError] = useState(() =>
    new URLSearchParams(location.search).get("error") === "oauth"
      ? "Google sign-in didn't complete. Please try again, or use your email instead."
      : ""
  );
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const fromLanding = location.state?.from?.pathname;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u =
        mode === "login"
          ? await login(email, password)
          : await register(email, password, name);
      navigate(
        u.active_profile_id ? fromLanding || "/dashboard" : "/profile/resume",
        {
          replace: true,
          state: {
            ...(mode === "register" ? { justRegistered: true } : {}),
            // Upload the resume first, then carry on to where they were headed (e.g. connecting the extension).
            ...(!u.active_profile_id && fromLanding ? { next: fromLanding } : {}),
          },
        }
      );
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas canvas-grain relative" data-testid="login-page">
      <div className="absolute inset-0 aurora-glow opacity-70 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-8">
        <Logo />
      </div>
      <div className="relative z-10 max-w-md mx-auto px-6 mt-16 md:mt-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="card-elev p-8 md:p-10"
        >
          <p className="eyebrow eyebrow-mint">{mode === "login" ? "Welcome back" : "Create account"}</p>
          <h1 className="mt-3 font-heading font-semibold text-ink text-[34px] tracking-tight leading-[1.05]">
            {mode === "login" ? "Sign in to GuMi." : "Start with GuMi."}
          </h1>
          <p className="mt-3 font-body text-[14.5px] text-ink-soft leading-[1.6]">
            {mode === "login"
              ? "Pick up where you left off. Your watchlist and analyses are waiting."
              : "3 free analyses every month. No credit card required."}
          </p>

          {googleOAuthEnabled && (
            <>
              <button
                onClick={startGoogleAuth}
                data-testid="google-auth-btn"
                className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full border border-line bg-white hover:bg-canvas2 px-6 py-3.5 font-body text-[14px] font-medium text-ink transition-colors focus-ring"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <div className="mt-6 flex items-center gap-3">
                <div className="hairline flex-1" />
                <span className="font-body text-[11px] uppercase tracking-[0.2em] text-ink-muted">or</span>
                <div className="hairline flex-1" />
              </div>
            </>
          )}

          <form onSubmit={submit} className={`${googleOAuthEnabled ? "mt-6" : "mt-8"} space-y-4`} noValidate data-testid={`${mode}-form`}>
            {mode === "register" && (
              <Field
                label="Name"
                value={name}
                onChange={setName}
                placeholder="Ada Lovelace"
                testId="auth-name"
              />
            )}
            <Field
              type="email"
              label="Email"
              value={email}
              onChange={setEmail}
              placeholder="you@company.com"
              required
              testId="auth-email"
            />
            <Field
              type="password"
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="At least 8 characters"
              required
              testId="auth-password"
            />
            {error && (
              <p className="font-body text-[13px] text-coral" data-testid="auth-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy hover:bg-navy-hover disabled:opacity-60 text-white px-6 py-3.5 font-body text-[14px] font-medium transition-all hover:scale-[0.98] focus-ring"
            >
              {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
            {mode === "register" && (
              <p
                className="font-body text-center"
                style={{ fontSize: 11, color: "#8a8880" }}
                data-testid="auth-legal-notice"
              >
                By creating an account, you agree to our{" "}
                <Link to="/terms" style={{ color: "#8a8880", textDecoration: "underline" }}>
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" style={{ color: "#8a8880", textDecoration: "underline" }}>
                  Privacy Policy
                </Link>
                .
              </p>
            )}
          </form>

          <p className="mt-6 font-body text-[13px] text-ink-soft text-center">
            {mode === "login" ? (
              <>
                New to GuMi?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-mint-deep font-medium hover:underline"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have one?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-mint-deep font-medium hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </motion.div>

        <p className="mt-8 text-center font-body text-[12px] text-ink-muted">
          <Link to="/" className="hover:text-ink transition-colors">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", placeholder, required, testId }) => (
  <label className="block">
    <span className="block eyebrow text-[10px] mb-2">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      data-testid={testId}
      className="w-full bg-white border border-line rounded-xl px-4 py-3 font-body text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-navy focus:ring-2 focus:ring-navy/10 transition"
    />
  </label>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.71-1.57 2.69-3.89 2.69-6.62z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);
